import apis from "../../utils/apis";
import { handleApiError } from "./utils/utils";
import { showFeedbackModal } from "./UserFeedbackActions";
import { SET_LICENSE_ACCESS, SET_AVAILABLE_PLANS } from "../reducer/licenseReducer";
import { translate } from '../../i18n/translate';

/* ─────────────────────────────────────────────────────────
   Types
   ───────────────────────────────────────────────────────── */

export interface LicensePlan {
  id: number;
  name: string;
  display_name: string;
  type: "base" | "addon" | "trial" | "pilot";
  org_type: string | null;
  description: string;
  detailed_description: string;
  price_monthly: number;
  price_annual: number;
  max_seats: number | null;
  is_per_seat: boolean;
  feature_gates: {
    sections: string[];
    ai_features: string[];
    limits: Record<string, number | null>;
  };
  is_active: boolean;
  sort_order: number;
  team_discount_percent: number;
  stripe_product_id?: string;
  stripe_price_id_monthly?: string;
  stripe_price_id_annual?: string;
}

export interface OrgLicenseAccess {
  price_currency?: string | null;
  billing_model?: 'bundle' | 'usage' | string;
  has_base_seat: boolean;
  licenses: Array<{
    id: number;
    license_id: number;
    license_name: string;
    display_name: string;
    type: string;
    is_active: boolean;
    activated_at: string;
    expires_at: string | null;
    stripe_subscription_id: string | null;
    seat_count: number;
  }>;
  /**
   * THE canonical entitlement lists — read these, not the API's field names.
   *
   * `/licensing/my-access` returns the same sections under three keys
   * (`accessible_sections`, `licensed_sections`, `effective_features.sections`)
   * and the store keeps ONE. Naming it at the top level means a reader never
   * has to know which of the three the normaliser happened to pick, and a
   * `console.log` of the store answers "what is this org entitled to" without
   * anyone having to guess at nesting.
   */
  sections: string[];
  ai_features: string[];
  effective_features: {
    sections: string[];
    ai_features: string[];
    limits: Record<string, number | null>;
  };
  license_info: {
    plan_name: string;
    plan_display_name: string;
    plan_type: string;
    active_addons: string[];
    user_addons?: string[];
    expires_at: string | null;
    days_remaining: number | null;
    is_expiring_soon: boolean;
    has_past_due_payment?: boolean;
    seat_count: number;
    seats_used: number;
    seats_available: number | null;
    has_base_seat?: boolean;
  } | null;
}

export interface LicenseStatus {
  has_active_license: boolean;
  is_trial: boolean;
  trial_days_remaining: number | null;
  base_plan: string | null;
  display_name: string | null;
  plan_type: string | null;
  active_addons: string[];
  expires_at: string | null;
  days_remaining: number | null;
  seat_count: number;
  user_count: number;
  seats_available: number | null;
}

/* ─────────────────────────────────────────────────────────
   Helpers — transform backend responses
   ───────────────────────────────────────────────────────── */

/** Backend plan → frontend LicensePlan (nest sections/ai_features/limits into feature_gates) */
function normalizePlan(raw: any): LicensePlan {
  return {
    id: raw.id,
    name: raw.name,
    display_name: raw.display_name || raw.name,
    type: raw.type,
    org_type: raw.org_type ?? null,
    description: raw.description || "",
    detailed_description: raw.detailed_description || "",
    price_monthly: raw.price_monthly ?? 0,
    price_annual: raw.price_annual ?? 0,
    max_seats: raw.max_seats ?? null,
    is_per_seat: raw.is_per_seat ?? false,
    feature_gates: raw.feature_gates ?? {
      sections: raw.sections || [],
      ai_features: raw.ai_features || [],
      limits: raw.limits || {},
    },
    is_active: raw.is_active ?? true,
    sort_order: raw.sort_order ?? 0,
    team_discount_percent: raw.team_discount_percent ?? 0,
    stripe_product_id: raw.stripe_product_id,
    stripe_price_id_monthly: raw.stripe_price_id_monthly,
    stripe_price_id_annual: raw.stripe_price_id_annual,
  };
}

/**
 * Backend /my-access → frontend OrgLicenseAccess.
 *
 * Reads `accessible_sections` first (what THIS user may reach, seat and
 * add-ons applied), then `licensed_sections` (what the ORG holds), then the
 * nested `effective_features.sections` — the response carries all three and
 * they agree, but a partial response might not.
 */
function normalizeAccess(raw: any): OrgLicenseAccess {
  /* Resolved ONCE, then written to both places, so the top-level lists and
     the nested ones can never disagree about what this org holds. */
  const sections: string[] =
    raw.accessible_sections ||
    raw.licensed_sections ||
    raw.effective_features?.sections ||
    [];
  const aiFeatures: string[] =
    raw.ai_features || raw.effective_features?.ai_features || [];
  const limits = raw.limits || raw.effective_features?.limits || {};

  return {
    has_base_seat: raw.has_base_seat ?? true,
    licenses: raw.licenses || [],
    sections,
    ai_features: aiFeatures,
    /* Kept for the readers that already walk it, and identical by
       construction to the canonical pair above. */
    effective_features: { sections, ai_features: aiFeatures, limits },
    license_info: raw.license_info || null,
  };
}

/** The org's sections, wherever a caller happens to hold the access object. */
export const accessSections = (access: OrgLicenseAccess | null | undefined): string[] =>
  access?.sections ?? access?.effective_features?.sections ?? [];

export const accessAiFeatures = (access: OrgLicenseAccess | null | undefined): string[] =>
  access?.ai_features ?? access?.effective_features?.ai_features ?? [];

/**
 * Is this a licence payload we can gate on?
 *
 * EVERY plan grants at least `dashboard` — there is no such thing as a
 * licensed org with zero sections. So an empty section list is not "this org
 * may see nothing"; it is a response we should not have believed: a bootstrap
 * race where the PONS session cookie was not yet established, a partial body,
 * a degraded upstream. Recording it as authoritative is what turned one bad
 * response into "your plan does not include Matters" on a firm holding PONS
 * Full with all 21 sections.
 *
 * Treated as NOT LOADED rather than as a denial, everywhere: the gate hook,
 * the request guard, and here, where it stops the bad payload being written at
 * all so a later good one is not shadowed by it.
 */
export function isUsableAccess(access: OrgLicenseAccess | null | undefined): boolean {
  /* Derived from the SAME list the gates check, so "usable" and "what is
     allowed" cannot drift apart. */
  return !!access && accessSections(access).length > 0;
}

/** Backend /status → frontend LicenseStatus */
function normalizeStatus(raw: any): LicenseStatus {
  return {
    has_active_license: raw.has_active_license ?? false,
    is_trial: raw.plan_type === "trial",
    trial_days_remaining: raw.plan_type === "trial" ? raw.days_remaining : null,
    base_plan: raw.license_name || raw.base_plan || null,
    display_name: raw.display_name || null,
    plan_type: raw.plan_type || null,
    active_addons: raw.active_addons || [],
    expires_at: raw.expires_at || null,
    days_remaining: raw.days_remaining ?? null,
    seat_count: raw.seat_count ?? 0,
    user_count: raw.user_count ?? 0,
    seats_available: raw.seats_available ?? null,
  };
}

/* ─────────────────────────────────────────────────────────
   Backend API — licensing data (read-only, via backend)
   ───────────────────────────────────────────────────────── */

/** GET /licensing/plans — available plans for the org's type */
export const fetchAvailablePlans = () => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({ method: "GET", url: "/licensing/plans" });
      if (resp && resp.status === 200) {
        const raw = resp.data?.data || resp.data || {};
        const result = {
          plans: (raw.base_plans || raw.plans || []).map(normalizePlan),
          addons: (raw.addons || []).map(normalizePlan),
        };
        dispatch({ type: SET_AVAILABLE_PLANS, payload: result });
        return result;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** GET /licensing/my-access — current org's active licenses + effective features */
export const fetchMyAccess = () => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({ method: "GET", url: "/licensing/my-access" });
      if (resp && resp.status === 200) {
        const raw = resp.data?.data || resp.data || {};
        const access = normalizeAccess(raw);
        /*
          A 200 carrying no sections is not an answer, it is a miss — see
          `isUsableAccess`. Leaving the store untouched keeps `loaded` false,
          which every gate reads as "not yet" and lets through, instead of
          writing a payload that locks the whole product.
        */
        if (!isUsableAccess(access)) {
          console.warn('[licensing] /my-access returned no sections; not gating on it.');
          return null;
        }
        dispatch({ type: SET_LICENSE_ACCESS, payload: access });
        return access;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** GET /licensing/status — quick status check */
export const fetchLicenseStatus = () => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({ method: "GET", url: "/licensing/status" });
      if (resp && resp.status === 200) {
        const raw = resp.data?.data || resp.data || {};
        return normalizeStatus(raw);
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/* ─────────────────────────────────────────────────────────
   Payments API — subscription actions (write, via payments)
   ───────────────────────────────────────────────────────── */

/** POST /api/subscriptions/checkout */
export const createSubscriptionCheckout = (params: {
  license_ids: number[];
  email: string;
  billing_period: "monthly" | "annual";
  seat_count: number;
  success_url: string;
  cancel_url: string;
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/checkout",
        data: params,
      });
      if (resp && resp.status === 200) {
        return resp.data as { session_id: string; url: string };
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /api/subscriptions/change-plan */
export const changePlan = (params: {
  new_license_id: number;
  billing_period: "monthly" | "annual";
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/change-plan",
        data: params,
      });
      if (resp && resp.status === 200) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate("Plan changed successfully"),
            modalType: "success",
            duration: 3000,
          })
        );
        return resp.data;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /api/subscriptions/add-addon */
export const addAddon = (params: {
  addon_license_id: number;
  billing_period: "monthly" | "annual";
  seat_count?: number;
  apply_team_discount?: boolean;
  email?: string;
  success_url?: string;
  cancel_url?: string;
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/add-addon",
        data: params,
      });
      if (resp && resp.status === 200) {
        // If the backend couldn't add directly (no Stripe sub), it returns a checkout URL
        if (resp.data.requires_checkout && resp.data.url) {
          return resp.data as { requires_checkout: true; session_id: string; url: string };
        }
        const paymentStatus = resp.data.payment_status || "active";
        if (paymentStatus === "active") {
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: translate("Add-on activated successfully"),
              modalType: "success",
              duration: 3000,
            })
          );
        } else {
          dispatch(
            showFeedbackModal({
              showModal: true,
              message: translate("Add-on payment is processing — it will activate shortly"),
              modalType: "info",
              duration: 4000,
            })
          );
        }
        return { ...resp.data, payment_status: paymentStatus };
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /payments/subscriptions/verify-addon-payment — poll for pending addon activation */
export const verifyAddonPayment = (orgLicenseId: number) => {
  return async (_dispatch: any): Promise<{ payment_status: string } | null> => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/verify-addon-payment",
        data: { org_license_id: orgLicenseId },
      });
      if (resp && resp.status === 200) {
        return resp.data as { payment_status: string; message: string };
      }
      return null;
    } catch {
      return null;
    }
  };
};

/** POST /api/subscriptions/cancel */
export const cancelSubscription = (params: {
  at_period_end: boolean;
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/cancel",
        data: params,
      });
      if (resp && resp.status === 200) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: params.at_period_end
              ? translate("Subscription will cancel at end of billing period")
              : translate("Subscription cancelled"),
            modalType: "success",
            duration: 4000,
          })
        );
        return resp.data;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /api/subscriptions/portal */
export const openBillingPortal = (params: {
  return_url: string;
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/portal",
        data: params,
      });
      if (resp && resp.status === 200) {
        return resp.data as { url: string };
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /api/subscriptions/sync — sync Stripe subscription into DB (local dev) */
export const syncSubscription = () => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/sync",
        data: {},
      });
      if (resp && resp.status === 200) {
        // Refresh license data after sync
        dispatch(fetchMyAccess());
        dispatch(fetchAvailablePlans());
        return resp.data;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

/** POST /api/subscriptions/payment-methods — get org's payment methods from Stripe */
export const fetchPaymentMethods = () => {
  return async (dispatch: any): Promise<PaymentMethod[]> => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/payment-methods",
        data: {},
      });
      if (resp && resp.status === 200) {
        return resp.data?.payment_methods || [];
      }
      return [];
    } catch (err) {
      // Silently fail — billing card will just show generic text
      return [];
    }
  };
};

export interface Invoice {
  id: string;
  number: string | null;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  lines: { description: string; amount: number; currency: string }[];
}

/** POST /api/subscriptions/invoices — get org's invoices from Stripe */
export const fetchInvoices = () => {
  return async (dispatch: any): Promise<Invoice[]> => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/invoices",
        data: {},
      });
      if (resp && resp.status === 200) {
        return resp.data?.invoices || [];
      }
      return [];
    } catch (err) {
      return [];
    }
  };
};

/** POST /licensing/assign-trial — assign a trial license to the user's org */
export const assignTrialLicense = () => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({ method: "POST", url: "/licensing/assign-trial" });
      if (resp && resp.status === 200) {
        const raw = resp.data?.data || resp.data || {};
        return raw;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/* ─────────────────────────────────────────────────────────
   Seat Management & Add-on Assignments
   ───────────────────────────────────────────────────────── */

export interface AssignedUser {
  user_id: number;
  name: string;
  email: string;
  assigned_at: string;
}

export interface AddonAssignment {
  org_license_id: number;
  license_id: number;
  name?: string;
  display_name: string;
  type?: string;
  quantity: number;
  assigned_count: number;
  available_seats: number;
  assigned_users: AssignedUser[];
}

export interface OrgMember {
  id: number;
  user_id?: number;
  name: string;
  first_name?: string;
  last_name?: string;
  email: string;
  role: string | null;
  is_org_admin?: boolean;
}

export interface AssignmentsResponse {
  base_plan: AddonAssignment | null;
  addons: AddonAssignment[];
  members: OrgMember[];
}

/** POST /api/subscriptions/update-seats — update seat count for an org license */
export const updateSeats = (params: {
  org_license_id: number;
  new_quantity: number;
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/payments/subscriptions/update-seats",
        data: params,
      });
      if (resp && resp.status === 200) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: `Seats updated to ${params.new_quantity}`,
            modalType: "success",
            duration: 3000,
          })
        );
        return resp.data;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** GET /licensing/assignments — get addon assignments for the org */
export const fetchAddonAssignments = () => {
  return async (dispatch: any): Promise<AssignmentsResponse | null> => {
    try {
      const resp = await apis({ method: "GET", url: "/licensing/assignments" });
      if (resp && resp.status === 200) {
        const raw = resp.data?.data || resp.data || {};
        // Normalize members: backend returns user_id, frontend uses id
        if (raw.members) {
          raw.members = raw.members.map((m: any) => ({
            ...m,
            id: m.id ?? m.user_id,
          }));
        }
        return raw as AssignmentsResponse;
      }
      return null;
    } catch (err) {
      return null;
    }
  };
};

/** POST /licensing/assign-user — assign user to an addon */
export const assignUserToAddon = (params: {
  org_license_id: number;
  user_id: number;
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/licensing/assign-user",
        data: params,
      });
      if (resp && resp.status === 200) {
        return resp.data?.data || resp.data;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /licensing/revoke-user — revoke user from an addon */
export const revokeUserFromAddon = (params: {
  org_license_id: number;
  user_id: number;
}) => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/licensing/revoke-user",
        data: params,
      });
      if (resp && resp.status === 200) {
        return resp.data?.data || resp.data;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /licensing/bulk-assign — assign base plan seats + optional addons in one call */
export const bulkAssignSeats = (userIds: number[], addonOrgLicenseIds?: number[]) => {
  return async (dispatch: any) => {
    try {
      const payload: Record<string, any> = { user_ids: userIds };
      if (addonOrgLicenseIds && addonOrgLicenseIds.length > 0) {
        payload.addon_org_license_ids = addonOrgLicenseIds;
      }
      const resp = await apis({
        method: "POST",
        url: "/licensing/bulk-assign",
        data: payload,
      });
      if (resp && resp.status === 200) {
        const result = resp.data?.data || resp.data;
        return result;
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** GET /licensing/trail-usage — current usage vs Trail plan limits */
export const fetchTrailUsage = () => {
  return async (dispatch: any) => {
    try {
      const resp = await apis({ method: "GET", url: "/licensing/trail-usage" });
      if (resp && resp.status === 200) {
        return resp.data?.data || resp.data;
      }
      return null;
    } catch (err) {
      return null;
    }
  };
};

/** POST /licensing/feature-request — non-admin requests feature access from org admin */
export const requestFeatureFromAdmin = (section: string, message?: string) => {
  return async (dispatch: any): Promise<boolean> => {
    try {
      const resp = await apis({
        method: "POST",
        url: "/licensing/feature-request",
        data: { section, message: message || "" },
      });
      if (resp && resp.status === 200) {
        const alreadyPending = resp.data?.already_pending;
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: alreadyPending
              ? translate("You already have a pending request for this feature")
              : translate("Request sent to your organization admin"),
            modalType: "success",
            duration: 4000,
          })
        );
        return true;
      }
      handleApiError(resp, dispatch);
      return false;
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};

/* ─────────────────────────────────────────────────────────
   Feature Requests — Admin Management
   ───────────────────────────────────────────────────────── */

export interface FeatureRequestItem {
  id: number;
  section: string;
  section_display: string;
  message: string | null;
  status: "pending" | "approved" | "denied";
  requested_by: { id: number; name: string; email: string };
  resolved_by: { id: number; name: string; email: string } | null;
  resolved_at: string | null;
  admin_note: string | null;
  created_at: string;
}

/** GET /licensing/feature-requests — admin lists all feature requests for their org */
export const fetchFeatureRequests = (status?: string) => {
  return async (dispatch: any): Promise<FeatureRequestItem[] | null> => {
    try {
      const url = status && status !== "all"
        ? `/licensing/feature-requests?status=${status}`
        : "/licensing/feature-requests";
      const resp = await apis({ method: "GET", url });
      if (resp && resp.status === 200) {
        return (resp.data?.data || []) as FeatureRequestItem[];
      }
      handleApiError(resp, dispatch);
      return null;
    } catch (err) {
      handleApiError(err, dispatch);
      return null;
    }
  };
};

/** POST /licensing/feature-requests/:id/approve — admin approves a request */
export const approveFeatureRequest = (requestId: number, note?: string) => {
  return async (dispatch: any): Promise<boolean> => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/licensing/feature-requests/${requestId}/approve`,
        data: { note: note || "" },
      });
      if (resp && resp.status === 200) {
        const detail = resp.data?.assignment_detail;
        const msg = detail
          ? `Request approved — ${detail}`
          : "Request approved — user has been notified";
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: msg,
            modalType: "success",
            duration: 4000,
          })
        );
        // Refresh addon assignments so seat counts update
        dispatch(fetchMyAccess());
        return true;
      }
      handleApiError(resp, dispatch);
      return false;
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};

/** POST /licensing/feature-requests/:id/deny — admin denies a request */
export const denyFeatureRequest = (requestId: number, note?: string) => {
  return async (dispatch: any): Promise<boolean> => {
    try {
      const resp = await apis({
        method: "POST",
        url: `/licensing/feature-requests/${requestId}/deny`,
        data: { note: note || "" },
      });
      if (resp && resp.status === 200) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: translate("Request denied — user has been notified"),
            modalType: "success",
            duration: 3000,
          })
        );
        return true;
      }
      handleApiError(resp, dispatch);
      return false;
    } catch (err) {
      handleApiError(err, dispatch);
      return false;
    }
  };
};
